import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, Tag, Percent, Layers, X } from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

import API from '../api_config';

export default function PromotionsPage() {
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    promotion_type: 'simple',
    product_id: '',
    min_quantity: '',
    requirements: [], 
    promo_price: '',
    is_active: true
  });

  // Combo Builder State
  const [reqProductId, setReqProductId] = useState('');
  const [reqQuantity, setReqQuantity] = useState('1');

  const fetchPromotions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/promotions`);
      setPromotions(response.data);
    } catch (error) {
      toast.error('Error al cargar promociones');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
    } catch (error) {
        console.error("Error fetching products", error);
    }
  }, []);

  useEffect(() => {
    fetchPromotions();
    fetchProducts();
  }, [fetchPromotions, fetchProducts]);

  const addRequirement = () => {
      if(!reqProductId) return;
      const product = products.find(p => p.id === reqProductId);
      setFormData({
          ...formData,
          requirements: [...(formData.requirements || []), { product_id: reqProductId, quantity: parseInt(reqQuantity), product_name: product?.name }]
      });
      setReqProductId('');
      setReqQuantity('1');
  };
  
  const removeRequirement = (index) => {
      const newReqs = [...formData.requirements];
      newReqs.splice(index, 1);
      setFormData({...formData, requirements: newReqs});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
          ...formData,
          promo_price: parseFloat(formData.promo_price)
      };

      if (formData.promotion_type === 'simple') {
          payload.min_quantity = parseInt(formData.min_quantity);
          payload.requirements = null; // Ensure clean data
      } else {
          payload.product_id = null;
          payload.min_quantity = null;
          if (!payload.requirements || payload.requirements.length === 0) {
              toast.error('Agrega al menos un producto al combo');
              return;
          }
      }

      if (editingPromo) {
        await axios.put(`${API}/promotions/${editingPromo.id}`, payload);
        toast.success('Promoción actualizada');
      } else {
        await axios.post(`${API}/promotions`, payload);
        toast.success('Promoción creada');
      }
      setIsFormOpen(false);
      setEditingPromo(null);
      fetchPromotions();
    } catch (error) {
      toast.error('Error al guardar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "Esto eliminará la promoción permanentemente",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API}/promotions/${id}`);
        fetchPromotions();
        toast.success('Promoción eliminada');
      } catch (error) {
        toast.error('No se pudo eliminar la promoción');
      }
    }
  };

  const openCreateModal = () => {
    setEditingPromo(null);
    setFormData({ 
        name: '', 
        promotion_type: 'simple', 
        product_id: '', 
        min_quantity: '', 
        requirements: [], 
        promo_price: '', 
        is_active: true 
    });
    setReqProductId('');
    setReqQuantity('1');
    setIsFormOpen(true);
  };

  const openEditModal = (promo) => {
    setEditingPromo(promo);
    setFormData({
      name: promo.name,
      promotion_type: promo.promotion_type || 'simple',
      product_id: promo.product_id || '',
      min_quantity: promo.min_quantity || '',
      requirements: promo.requirements || [],
      promo_price: promo.promo_price,
      is_active: promo.is_active
    });
    setReqProductId('');
    setReqQuantity('1');
    setIsFormOpen(true);
  };

  if (loading) return <div className="p-8 text-center">Cargando promociones...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Promociones</h1>
          <p className="text-muted-foreground mt-2">Configura descuentos por volumen y combos</p>
        </div>
        
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
            <DialogTrigger asChild>
                <Button onClick={openCreateModal} className="shadow-lg">
                    <Plus className="h-4 w-4 mr-2" />
                    Nueva Promoción
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{editingPromo ? 'Editar Promoción' : 'Nueva Promoción'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label>Nombre de la Promoción</Label>
                        <Input 
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="Ej: Combo Almuerzo"
                            required
                        />
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Tipo de Promoción</Label>
                        <Tabs 
                            value={formData.promotion_type} 
                            onValueChange={(val) => setFormData({...formData, promotion_type: val})}
                            className="w-full"
                        >
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="simple">Simple (Volumen)</TabsTrigger>
                                <TabsTrigger value="combo">Combo (Compuesto)</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>

                    {formData.promotion_type === 'simple' ? (
                        <div className="space-y-4 border p-4 rounded-md">
                             <div className="space-y-2">
                                <Label>Producto</Label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={formData.product_id}
                                    onChange={(e) => setFormData({...formData, product_id: e.target.value})}
                                    required={formData.promotion_type === 'simple'}
                                >
                                    <option value="">Selecciona producto...</option>
                                    {products.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} (${p.price})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Cantidad Mínima</Label>
                                    <Input 
                                        type="number"
                                        value={formData.min_quantity}
                                        onChange={(e) => setFormData({...formData, min_quantity: e.target.value})}
                                        min="2"
                                        required={formData.promotion_type === 'simple'}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Precio Unitario Promo</Label>
                                    <Input 
                                        type="number"
                                        value={formData.promo_price}
                                        onChange={(e) => setFormData({...formData, promo_price: e.target.value})}
                                        required
                                    />
                                    <p className="text-xs text-muted-foreground">Nuevo precio por cada unidad</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 border p-4 rounded-md">
                            <div className="space-y-2">
                                <Label>Configurar Combo</Label>
                                <div className="flex gap-2 items-end">
                                    <div className="flex-1 space-y-1">
                                        <Label className="text-xs">Producto</Label>
                                        <select
                                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                                            value={reqProductId}
                                            onChange={(e) => setReqProductId(e.target.value)}
                                        >
                                            <option value="">Seleccionar...</option>
                                            {products.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="w-20 space-y-1">
                                        <Label className="text-xs">Cant.</Label>
                                        <Input 
                                            type="number" 
                                            value={reqQuantity} 
                                            onChange={(e) => setReqQuantity(e.target.value)}
                                            className="h-9"
                                            min="1"
                                        />
                                    </div>
                                    <Button type="button" size="sm" onClick={addRequirement} disabled={!reqProductId}>
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </div>
                                
                                <div className="bg-muted/50 rounded-md p-2 min-h-[100px] space-y-2 mt-2">
                                    {formData.requirements && formData.requirements.length > 0 ? (
                                        formData.requirements.map((req, idx) => (
                                            <div key={idx} className="flex justify-between items-center bg-background p-2 rounded border text-sm">
                                                <span>{req.quantity}x <strong>{req.product_name || 'Producto'}</strong></span>
                                                <Button type="button" variant="ghost" size="sm" onClick={() => removeRequirement(idx)}>
                                                    <X className="h-3 w-3 text-red-500" />
                                                </Button>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-xs text-center text-muted-foreground py-8">Agrega productos al combo</p>
                                    )}
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Precio Total del Combo</Label>
                                <Input 
                                    type="number"
                                    value={formData.promo_price}
                                    onChange={(e) => setFormData({...formData, promo_price: e.target.value})}
                                    required={formData.promotion_type === 'combo'}
                                    className="text-lg font-bold text-green-600"
                                />
                                <p className="text-xs text-muted-foreground">Valor total por todo el paquete</p>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center space-x-2 pt-2">
                        <Switch 
                            checked={formData.is_active}
                            onCheckedChange={(c) => setFormData({...formData, is_active: c})}
                        />
                        <Label>Promoción Activa</Label>
                    </div>
                    
                    <div className="flex justify-end space-x-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
                        <Button type="submit">{editingPromo ? 'Actualizar' : 'Crear'}</Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {promotions.map((promo) => (
          <Card key={promo.id} className={`hover:shadow-md transition-shadow ${!promo.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                    <div className={`p-2 rounded-full ${promo.promotion_type === 'combo' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                        {promo.promotion_type === 'combo' ? <Layers className="h-6 w-6" /> : <Tag className="h-6 w-6" />}
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-bold ${promo.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {promo.is_active ? 'ACTIVA' : 'INACTIVA'}
                    </div>
                </div>
                
                <h3 className="text-lg font-bold mb-1">{promo.name}</h3>
                <p className="text-xs font-bold text-muted-foreground uppercase mb-3">
                    {promo.promotion_type === 'combo' ? 'Combo / Pack' : 'Descuento por Volumen'}
                </p>
                
                <div className="bg-muted p-3 rounded-lg text-sm space-y-2 mb-4">
                    {promo.promotion_type === 'combo' ? (
                        <div className="text-xs space-y-1">
                            <p className="font-semibold mb-1">Incluye:</p>
                            {promo.requirements?.map((req, i) => (
                                <div key={i} className="flex justify-between border-b pb-1 last:border-0 border-border/50">
                                    <span>{req.quantity}x {req.product_name}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex justify-between">
                            <span>Producto:</span>
                            <span className="font-bold">{promo.product_name} (Min: {promo.min_quantity})</span>
                        </div>
                    )}
                    
                    <div className="flex justify-between pt-2 border-t border-border/50">
                        <span>Precio Final:</span>
                        <span className="font-bold text-green-600 text-lg">${promo.promo_price.toLocaleString()}</span>
                    </div>
                </div>

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(promo)}>
                        <Edit className="h-4 w-4 mr-2" /> Editar
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(promo.id)}>
                        <Trash2 className="h-4 w-4 mr-2" /> Eliminar
                    </Button>
                </div>
            </CardContent>
          </Card>
        ))}
        
        {promotions.length === 0 && (
            <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                <Percent className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No hay promociones configuradas</p>
                <Button variant="link" onClick={openCreateModal}>Crear la primera</Button>
            </div>
        )}
      </div>
    </div>
  );
}
